import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Volume2, VolumeX, Loader2, Headphones } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/apiClient";
import { createMix } from "@/lib/immersionMix";
import { logExperience } from "@/lib/experience";
import FollowDot from "@/components/immersion/FollowDot";
import DriftWords from "@/components/immersion/DriftWords";
import ImmersionCheckIn from "@/components/immersion/ImmersionCheckIn";

const BASE = process.env.REACT_APP_BACKEND_URL;

// Arrive → Enter → Immerse → Deepen → Integrate → Return → Space. Somewhere he goes, not something he watches.
export default function ImmersionV2({ env, phases, flash, topicId, topicTitle }) {
  const navigate = useNavigate();
  const [guide, setGuide] = useState(null);
  const [musicUrl, setMusicUrl] = useState(null);
  const [stage, setStage] = useState("entry"); // entry | arriving | immersed | space | done
  const [dot, setDot] = useState(false);
  const [words, setWords] = useState(false);
  const [muted, setMuted] = useState(false);
  const [askPhones, setAskPhones] = useState(false);
  const [progress, setProgress] = useState(0);
  const videoRef = useRef(null), mixRef = useRef(null), phaseRef = useRef(0), depthRef = useRef(1), fired = useRef({});

  useEffect(() => {
    api.get(`/immersions/${env.id}/guide`, { params: { topic: topicId } }).then((r) => setGuide(r.data)).catch(() => setGuide({}));
    api.get("/music/bed", { params: { kind: "immersion", ref: env.id } }).then((r) => setMusicUrl(r.data.url || "")).catch(() => setMusicUrl(""));
    return () => mixRef.current?.close();
  }, [env.id, topicId]);

  const begin = async () => {
    setStage("arriving");
    const mix = createMix({ musicUrl, binaural: guide.binaural, musicGain: guide.music_gain, duckGain: guide.duck_gain });
    mixRef.current = mix;
    try { await mix.load([guide.open, guide.close, guide.whisper, guide.checkin, ...(guide.mid || []).map((m) => m.audio)].filter(Boolean)); } catch {}
    mix.start();
    videoRef.current?.play().catch(() => {});
    setStage("immersed");
    setTimeout(() => setDot(true), 9000);
    setTimeout(async () => { await mix.speak(guide.open); setWords(true); fired.current.words = 1; mix.whisper(guide.whisper, guide.whisper_gain); }, 1500);
  };

  // Arrive/Enter/Immerse → Deepen (layers richer) → Integrate (layers simplify)
  const setArc = (p) => {
    const d = p > 0.75 ? 0.6 : p > 0.4 ? 1.5 : 1;
    if (d !== depthRef.current) { depthRef.current = d; mixRef.current?.setWhisper((guide.whisper_gain || 0.07) * (d > 1 ? 1.35 : d < 1 ? 0.6 : 1)); }
  };

  const closeOut = () => {
    if (fired.current.close) return;
    fired.current.close = 1; setWords(false);
    mixRef.current?.speak(guide?.close);
    logExperience("immersion", `${topicId}/${env.id}`, env.title);
  };

  const onTime = () => {
    const v = videoRef.current; if (!v || !guide) return;
    const dur = v.duration || env.duration, p = Math.min(1, v.currentTime / dur);
    setProgress(p);
    phaseRef.current = Math.min(4, Math.floor(p * 5));
    if (fired.current.words) setArc(p);
    (guide.mid || []).forEach((m, i) => { if (p >= m.at && !fired.current[`m${i}`]) { fired.current[`m${i}`] = 1; mixRef.current?.speak(m.audio); } });
    if (dur - v.currentTime < guide.close_before_end) closeOut();
  };
  const onEnded = () => { const late = !fired.current.close; closeOut(); setDot(false); setStage("space"); mixRef.current?.fadeOut(8); setTimeout(() => setStage("done"), late ? 9000 : 5000); };
  const toggleMute = () => { setMuted((m) => { mixRef.current?.setMuted(!m); return !m; }); };
  const exit = () => { mixRef.current?.close(); navigate(`/immersions/${topicId}`); };
  const accent = env.accent || "#4FB6C4";
  const ready = guide && musicUrl !== null;

  return (
    <div className="fixed inset-0 z-[70] bg-black overflow-hidden" data-testid="immersion-player" data-version="v2">
      <video ref={videoRef} src={`${BASE}${env.video_url}`} muted playsInline preload="auto" onTimeUpdate={onTime} onEnded={onEnded}
        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[3000ms]" style={{ opacity: stage === "entry" ? 0.35 : stage === "done" ? 0.15 : 1 }} data-testid="immersion-video" />
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.5) 100%)" }} />
      <FollowDot visible={dot && stage === "immersed"} speed={guide?.dot?.speed} size={guide?.dot?.size} accent={accent} />
      {phases && <DriftWords active={words && stage === "immersed"} phases={phases} flash={flash} phaseRef={phaseRef} depthRef={depthRef} layers={guide?.layers} />}

      <AnimatePresence>
        {stage === "entry" && (
          <motion.div exit={{ opacity: 0 }} transition={{ duration: 1.5 }} className="absolute inset-0 flex flex-col items-center justify-center text-center px-6" data-testid="immersion-entry">
            <p className="text-xs uppercase tracking-[0.25em] mb-3" style={{ color: accent }}>{topicTitle} · {env.theme}</p>
            <h1 className="font-serif text-4xl sm:text-6xl text-white mb-3">{env.title}</h1>
            <p className="text-white/70 max-w-sm mb-9 leading-relaxed">There's nothing you need to do. Breathe, relax, follow the dot.</p>
            {!askPhones ? (
              <Button size="lg" className="rounded-full px-9" onClick={() => setAskPhones(true)} disabled={!ready} data-testid="immersion-begin-btn">
                {ready ? "Begin immersion" : <Loader2 className="w-4 h-4 animate-spin" />}
              </Button>
            ) : (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="flex flex-col items-center" data-testid="immersion-headphones">
                <Headphones className="w-8 h-8 mb-3" style={{ color: accent }} />
                <p className="font-serif text-xl text-white mb-1">Headphones on?</p>
                <p className="text-sm text-white/60 max-w-xs mb-6">The binaural beats and whispers only reach you through headphones.</p>
                <Button size="lg" className="rounded-full px-9 mb-3" onClick={begin} data-testid="immersion-headphones-on">They're on · begin</Button>
                <button onClick={begin} className="text-xs text-white/50 hover:text-white transition-colors" data-testid="immersion-headphones-skip">Begin without</button>
              </motion.div>
            )}
          </motion.div>
        )}
        {stage === "arriving" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1.2 }} className="absolute inset-0 flex items-center justify-center">
            <motion.p animate={{ opacity: [0.35, 0.9, 0.35] }} transition={{ duration: 4, repeat: Infinity }} className="font-serif text-2xl text-white/90">Breathe…</motion.p>
            <p className="absolute bottom-[18%] text-xs uppercase tracking-[0.25em] text-white/50" data-testid="immersion-preparing">Preparing your sound…</p>
          </motion.div>
        )}
        {stage === "done" && (
          <ImmersionCheckIn iid={env.id} topicId={topicId} coach={guide?.coach} ask={() => mixRef.current?.speak(guide?.checkin)} onReturn={exit} />
        )}
      </AnimatePresence>

      {stage === "immersed" && (
        <>
          <div className="absolute top-0 inset-x-0 h-px bg-white/10"><div className="h-full transition-[width] duration-1000" style={{ width: `${progress * 100}%`, background: accent }} /></div>
          <button onClick={toggleMute} className="absolute bottom-6 left-6 w-10 h-10 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white/60 hover:text-white transition-colors" data-testid="immersion-mute">
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
        </>
      )}
      <button onClick={exit} className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white/60 hover:text-white transition-colors z-10" data-testid="immersion-exit"><X className="w-4 h-4" /></button>
    </div>
  );
}
